// ============================================================================
// Erro de sistema não vai para a tela (20/09/2026).
//
// Sete pontos vivos jogavam a mensagem crua do Supabase direto na cara dela:
// os quatro ramos de erro do `/auth`, o envio do link por email e as três telas
// pós-compra. São frases escritas para quem programa — "Database error saving
// new user", "Unable to validate email address: invalid format", "Email rate
// limit exceeded" — e quem as lê é uma avó de 55 anos que acabou de pagar e não
// está conseguindo entrar.
//
// Aqui cada família de erro vira uma frase em linguagem comum COM PRÓXIMO PASSO.
// O que não for reconhecido cai num texto que também diz o que fazer, em vez de
// repetir o problema. Nada de código de erro, nada de nome de tabela.
//
// O que NÃO passa por aqui: mensagens que o próprio app escreveu para ela
// ("This email doesn't have a purchase", "You already have an account"). Essas
// já estão na voz certa e são reconhecidas abaixo para seguirem intactas.
// ============================================================================

const SUPORTE = 'If it keeps happening, tap "Stuck? Talk to a person" below.';

interface Regra {
  /** Marcas da mensagem crua. Minúsculas, comparadas sem acento de maiúscula. */
  marcas: RegExp;
  texto: string;
}

const REGRAS: Regra[] = [
  {
    // A rede caiu no meio do caminho. É o mais comum no celular.
    marcas: /failed to fetch|network|connection failed|internet|timeout|timed out|econn|eof|fetch failed/i,
    texto: 'We could not reach the app. Check your connection and try again.',
  },
  {
    marcas: /\b5\d\d\b|internal server|service unavailable|bad gateway|database error/i,
    texto: 'Something went wrong on our side, not yours. Please try again in a minute.',
  },
  {
    marcas: /rate limit|too many requests|over_email_send_rate|429/i,
    texto: 'Too many tries in a row. Please wait a minute and try again.',
  },
  {
    marcas: /invalid format|unable to validate email|invalid email/i,
    texto: 'That email address does not look right. Please check it for a typo.',
  },
  {
    marcas: /already registered|already exists|user_already_exists/i,
    texto: 'You already have an account with this email. Use "Forgot password?" to get a 6 digit code.',
  },
  {
    marcas: /email not confirmed|not_confirmed/i,
    texto: 'Your email has not been confirmed yet. Check your inbox for our message.',
  },
  {
    marcas: /invalid login|invalid credentials|invalid email or password/i,
    texto: 'That password does not match. Use "Forgot password?" to get a 6 digit code.',
  },
  {
    marcas: /password.*(at least|too short|6 characters)/i,
    texto: 'Choose a password with at least 6 characters.',
  },
  {
    marcas: /expired|token.*(invalid|not found)|otp_expired/i,
    texto: 'That link has expired. Ask for a new one and use it within the hour.',
  },
];

/**
 * Frases que o próprio app escreveu para ela. Já estão na voz certa e passam
 * inteiras: reescrevê-las seria apagar informação verdadeira e específica.
 */
const JA_E_NOSSA =
  /purchase|paid with|restricted|access|blocked|refund|family|gift|6 digit|forgot password/i;

/**
 * Traduz uma mensagem de erro para algo que ela entende e possa agir.
 *
 * @param cru A mensagem que veio do sistema. Pode ser vazia.
 * @param padrao O que dizer quando nada é reconhecido.
 */
export function friendlyError(
  cru: unknown,
  padrao = `Something went wrong. Please try again. ${SUPORTE}`,
): string {
  const texto =
    typeof cru === 'string'
      ? cru
      : cru instanceof Error
        ? cru.message
        : typeof (cru as { message?: unknown })?.message === 'string'
          ? ((cru as { message: string }).message)
          : '';

  if (!texto.trim()) return padrao;

  // Mensagem nossa passa inteira, desde que não seja um despejo técnico.
  if (JA_E_NOSSA.test(texto) && !/\{|\}|error:|exception|stack|at \w+\./i.test(texto)) {
    return texto;
  }

  for (const regra of REGRAS) {
    if (regra.marcas.test(texto)) return regra.texto;
  }

  return padrao;
}
